/**
 * Published dashboard — the end-user view.
 *
 * VIEW ONLY. This component deliberately renders no table of contents, no data
 * source controls, no "add widget" affordance, and no builder navigation. The
 * only interactivity is what a consumer needs: refreshing data and reading it.
 *
 * Entitlement is checked by the caller (Portal) through
 * `moduleService.canAccessModule`, the same boundary Map App modules use.
 */

import { useMemo, useState } from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import type { AppModule } from '../types';
import DashboardGrid from './components/DashboardGrid';
import DashboardSelectorBar from './components/DashboardSelectorBar';
import { GRID_GAP } from './utils/dashboardUtils';
import { selectorFiltersFor, type SelectorState, type SelectorValue } from './utils/selectorRuntime';
import HeaderLogo from '../components/header/HeaderLogo';
import { getDashboardConfig } from './services/dashboardService';

/* A dashboard set to "Always dark" scopes the dark tokens to its own subtree,
   so it stays dark inside a light application; "Follow application" sets no
   attribute at all and simply inherits. Either way the ground comes from the
   `--bv-ground` token, which resolves against whichever scope applies. */

interface Props {
  module: AppModule;
  /** Omitted when the viewer was deep-linked rather than opened from the portal. */
  onBack?: () => void;
}

export default function DashboardViewer({ module, onBack }: Props) {
  const config = getDashboardConfig(module);
  // Remounting the grid re-runs every widget's query — the manual refresh.
  const [refreshToken, setRefreshToken] = useState(0);
  const dark = config.theme.mode === 'dark';
  const logoRight = config.theme.logoPosition === 'right';
  const { headerBgColor, headerTextColor } = config.theme;

  const logo = <HeaderLogo url={config.theme.logoUrl} size={config.theme.logoSize} />;

  // What an end user has picked in the header's own selectors — see
  // DashboardSelectorBar. Resolved to one WHERE clause per data source ONCE
  // here, so every widget below only has to look its own dataSourceId up in
  // a plain map rather than re-walk every selector itself.
  const [selectorValues, setSelectorValues] = useState<SelectorState>({});
  const selectorFilters = useMemo(
    () =>
      selectorFiltersFor(
        config.dataSources.map((source) => source.id),
        config.selectors,
        selectorValues,
      ),
    [config.dataSources, config.selectors, selectorValues],
  );
  const setSelectorValue = (id: string, value: SelectorValue | undefined) =>
    setSelectorValues((current) => {
      if (!value) {
        if (!(id in current)) return current;
        const next = { ...current };
        delete next[id];
        return next;
      }
      return { ...current, [id]: value };
    });

  return (
    <div
      data-theme={dark ? 'dark' : undefined}
      className="flex h-screen flex-col overflow-hidden"
      // A soft tinted ground so the glass cards have something to refract;
      // flat grey would make the frosting invisible. The dark board uses the
      // same idea with the tints dialled right down.
      style={{ background: 'var(--bv-ground)' }}
    >
      <header
        className="gl-panel flex flex-shrink-0 items-center gap-3 rounded-none border-x-0 border-t-0 px-5 py-3"
        // Inline, so a deliberate colour wins over the glass panel's own
        // translucent background/border without a second CSS class to track.
        style={headerBgColor ? { background: headerBgColor, borderColor: headerBgColor } : undefined}
      >
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to applications"
            className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100"
            style={headerTextColor ? { color: headerTextColor } : undefined}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}

        {/* Same mark, same rules as an application header -- see HeaderLogo. */}
        {!logoRight && logo}

        <div className="min-w-0 max-w-xs flex-shrink">
          <p
            className="text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: config.theme.accentColor }}
          >
            Dashboard
          </p>
          <h1
            className="truncate text-base font-semibold text-slate-900"
            style={headerTextColor ? { color: headerTextColor } : undefined}
          >
            {module.title}
          </h1>
        </div>

        {/* Fills whatever's left of THIS one row — see DashboardSelectorBar's
            own note. An empty `flex-1` when there are no selectors, same as
            the title's old `flex-1` used to be — the header looks exactly as
            it did before this existed. */}
        <DashboardSelectorBar
          selectors={config.selectors}
          dataSources={config.dataSources}
          values={selectorValues}
          onChange={setSelectorValue}
        />

        <button
          type="button"
          onClick={() => setRefreshToken((token) => token + 1)}
          className="gl-btn gl-btn--secondary flex-shrink-0 px-3 py-2 text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>

        {logoRight && logo}
      </header>

      <main
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        style={{ padding: GRID_GAP }}
      >
        {config.description && (
          <p
            className="max-w-3xl flex-shrink-0 text-sm text-slate-600"
            style={{ marginBottom: GRID_GAP }}
          >
            {config.description}
          </p>
        )}

        <div className="min-h-0 flex-1">
          <DashboardGrid
            key={refreshToken}
            widgets={config.widgets}
            dataSources={config.dataSources}
            refreshInterval={config.refreshInterval}
            accentColor={config.theme.accentColor}
            themeMode={config.theme.mode}
            dividerColor={config.theme.dividerColor}
            selectorFilters={selectorFilters}
          />
        </div>
      </main>
    </div>
  );
}
